import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DesktopDialog } from "../../../components/app/DesktopDialog";
import { displayToast } from "../../../components/app/AppToast";
import { Button } from "../../../components/ui/Button";
import { saveAcquisitionChannel } from "../../../domains/user/saveAcquisitionChannel";
import { acquisitionCardDismissedStore } from "../../../domains/user/stores/acquisitionCardStore";
import {
  ACQUISITION_CHANNELS,
  acquisitionChannelLabel,
  type AcquisitionChannelValue,
} from "../../../domains/user/utils/acquisitionChannels";
import { useStore } from "../../../lib/store";
import { cn } from "../../../lib/utils";

/**
 * "How did you hear about us?" on the JuisciGPT home (Figma Desktop
 * 5736:129165, 5736:129424): a card in the window's corner that leaves the page
 * in use. The signup no longer asks it, so the home asks it of every account
 * without an answer. The phone asks it in the conversation's composer band
 * (`AcquisitionSuggestionCard`). "Later" and the cross hide it for the session.
 *
 * Continue saves the answer. The card goes away once the reloaded user carries
 * it, which is the caller's condition for rendering this.
 */
export function DesktopAcquisitionCard(): React.JSX.Element | null {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useStore(acquisitionCardDismissedStore);
  // The pick, and the save in flight, through RHF (no useState here).
  const { watch, setValue, handleSubmit, formState } = useForm<{
    channel: AcquisitionChannelValue | null;
  }>({ defaultValues: { channel: null } });
  const selected = watch("channel");

  if (dismissed) return null;

  function dismiss(): void {
    setDismissed(true);
  }

  const submit = handleSubmit(async ({ channel }) => {
    if (channel === null)
      throw new Error("should not happen: Continue without a channel");
    if ((await saveAcquisitionChannel(channel)) === "failed")
      displayToast(t("Something went wrong. Please try again."), "error");
  });

  return (
    <DesktopDialog
      presentation="corner"
      title={t("How did you hear about us?")}
      header={{ layout: "inline" }}
      subtitle={null}
      onClose={dismiss}
      footer={
        <div className="flex w-full items-center pt-1">
          <Button
            variant="ghost"
            size="sm"
            className="flex-1"
            onClick={dismiss}
          >
            {t("Later")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            className="flex-1"
            disabled={selected === null || formState.isSubmitting}
            onClick={() => {
              void submit();
            }}
          >
            {t("Continue")}
          </Button>
        </div>
      }
    >
      <div className="flex w-full flex-col gap-1.5">
        {ACQUISITION_CHANNELS.map((channel) => (
          <button
            key={channel}
            type="button"
            aria-pressed={selected === channel}
            // The saved answer is the one picked when Continue was pressed: a
            // row tapped while it saves would be highlighted but not sent.
            disabled={formState.isSubmitting}
            className={cn(
              "flex h-9 w-full shrink-0 cursor-pointer items-center rounded-desktop-row border border-solid px-3.5 text-left font-dm-sans text-desktop-body-s font-medium tracking-ds text-ds-mint-900 transition-colors disabled:cursor-default",
              selected === channel
                ? "border-ds-mint-700 bg-ds-mint-50"
                : "border-ds-black/6 bg-ds-white"
            )}
            onClick={() => {
              setValue("channel", channel);
            }}
          >
            {acquisitionChannelLabel(t, channel)}
          </button>
        ))}
      </div>
    </DesktopDialog>
  );
}
